/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useEffect,
  useState,
  useRef,
  cloneElement,
  useCallback,
} from "react";
import classNames from "classnames";
import _ from "lodash";
import styles from "./index.module.scss";

const Tabs = (props) => {
  const {
    items,
    defaultActiveKey,
    activeKey,
    tabBarGutter = 16,
    tabBarStyle,
    tabBarClassName,
    activeTabClassName,
    contentClassName,
    onChange,
    onTabClick,
    className,
    disableShowDefault = false,
    destroyInactiveTabPane = false,
    ...restProps
  } = props;
  const defaultKey = defaultActiveKey ?? activeKey ?? items[0]?.key;
  const [_activeKey, _setActiveKey] = useState(
    disableShowDefault ? "" : defaultKey
  );
  const [shadow, setShadow] = useState({
    left: false,
    right: false,
  });
  const [indicatorPosition, setIndicatorPosition] = useState();
  const TabsRef = useRef(null);
  const renderedList = useRef([]);

  const getEleRectByKey = (key) => {
    const tab = TabsRef.current?.getElementsByClassName(key);
    if (tab) {
      const target = tab[0];
      return {
        width: target?.clientWidth ?? 0,
        left: target?.offsetLeft ?? 0,
      };
    }

    return {
      width: indicatorPosition.width ?? 0,
      left: indicatorPosition.left ?? 0,
    };
  };

  const onTabsScroll = useCallback(
    _.throttle(() => {
      const target = TabsRef.current;
      if (target) {
        const { scrollLeft, scrollWidth, clientWidth } = target;
        setShadow({
          left: scrollLeft > 0,
          right: scrollWidth - scrollLeft > clientWidth,
        });
      }
    }, 500),
    []
  );

  // 与外部传进来的 activeKey 同步
  useEffect(() => {
    if (activeKey) {
      _setActiveKey(activeKey);
    }
  }, [activeKey]);

  useEffect(() => {
    onTabsScroll();
    if (!_activeKey) {
      return;
    }
    const rect = getEleRectByKey(_activeKey);
    if (rect) {
      setIndicatorPosition({
        width: rect.width,
        left: rect.left,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_activeKey]);

  return (
    <div className={classNames(styles.nav)} {...restProps}>
      <div
        className={classNames(styles.tabsLayout, {
          [styles.leftShadow]: shadow.left,
          [styles.rightShadow]: shadow.right,
        })}
      >
        <div
          className={classNames(styles.tabs, className)}
          ref={TabsRef}
          onScroll={onTabsScroll}
        >
          {items.map(({ key, label }) => {
            return (
              <div
                key={key}
                className={classNames(styles.tab, tabBarClassName, key, {
                  [activeTabClassName]:
                    activeTabClassName && key === _activeKey,
                })}
                onClick={(e) => {
                  _setActiveKey(key);
                  onChange?.(key);
                  onTabClick?.(key, e);
                }}
                onMouseEnter={(e) => {
                  const target = e.target;
                  setIndicatorPosition({
                    width: target.clientWidth,
                    left: target.offsetLeft,
                  });
                }}
                onMouseLeave={() => {
                  if (!_activeKey) {
                    setIndicatorPosition({
                      width: 0,
                      left: indicatorPosition?.left ?? 0,
                    });
                    return;
                  }
                  const rect = getEleRectByKey(_activeKey);
                  if (rect) {
                    setIndicatorPosition({
                      width: rect.width,
                      left: rect.left,
                    });
                  }
                }}
                style={{
                  marginRight: tabBarGutter,
                  ...(tabBarStyle ?? {}),
                }}
              >
                {label}
              </div>
            );
          })}
          <div
            className={styles.indicator}
            style={{
              width: indicatorPosition?.width,
              left: indicatorPosition?.left,
            }}
          />
        </div>
      </div>
      {items.map(({ key, children, forceRender }) => {
        const active = _activeKey === key;
        const hasRendered = renderedList.current.includes(key);

        const recordRendered = () => {
          if (!hasRendered) {
            renderedList.current.push(key);
          }
        };

        const content = (
          <div
            key={key}
            className={classNames(styles.content, contentClassName)}
          >
            {children}
          </div>
        );

        if (!active) {
          // 不是当前激活的 tab，且设置了销毁dom，则不渲染
          if (destroyInactiveTabPane) {
            return null;
          }

          if (hasRendered || forceRender) {
            recordRendered();
            return cloneElement(content, {
              style: { display: "none" },
            });
          }

          return null;
        }

        /* ---- active ---- */
        recordRendered();
        return content;
      })}
    </div>
  );
};

export default Tabs;
